import { Injectable, inject } from '@angular/core';
import {
  Auth,
  authState,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User,
  UserCredential,
  getIdTokenResult,
  IdTokenResult,
  onAuthStateChanged,
  deleteUser,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
  reauthenticateWithCredential,
  EmailAuthProvider,
  reauthenticateWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from '@angular/fire/auth';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { DatabaseService, UserSettings } from './database.service';
import { AnalyticsService } from './analytics.service';
import type { CustomClaims } from '@creditpath/shared-types';

export type CustomUserClaims = CustomClaims;

function authTelemetryCode(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }
  return 'unknown';
}

/** Single GA4 `user_role` dimension from JWT `roles` (admin > advisor > lender). */
function primaryUserRole(roles: string[] | undefined): string {
  if (!roles?.length) {
    return 'unknown';
  }
  if (roles.includes('admin')) {
    return 'admin';
  }
  if (roles.includes('advisor')) {
    return 'advisor';
  }
  if (roles.includes('lender')) {
    return 'lender';
  }
  return roles[0];
}

export interface AppUser extends User {
  claims?: CustomUserClaims;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private db = inject(DatabaseService);
  private analytics = inject(AnalyticsService);

  // Observable for the current user, potentially with custom claims
  private currentUserSubject = new BehaviorSubject<AppUser | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  // Observable for just the custom claims
  private customClaimsSubject = new BehaviorSubject<CustomUserClaims | null>(null);
  customClaims$ = this.customClaimsSubject.asObservable();

  // Observable for user settings
  private userSettingsSubject = new BehaviorSubject<UserSettings | null>(null);
  userSettings$ = this.userSettingsSubject.asObservable();

  // currentUser$ starts with a placeholder null before Firebase has restored the
  // session, so take(1) on it reads "signed out" on a cold page load (BUG-6).
  private authResolvedSubject = new BehaviorSubject<boolean>(false);

  /**
   * The current user, emitted only once Firebase has answered (a user with
   * claims loaded, or a definite null). Use this, not currentUser$, for one-shot
   * "is anyone signed in?" checks such as route guards.
   */
  readonly resolvedUser$: Observable<AppUser | null> = this.authResolvedSubject.pipe(
    filter((resolved) => resolved),
    switchMap(() => this.currentUser$),
  );

  constructor() {
    // Listen to authentication state changes
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user);
          const claims = idTokenResult.claims as CustomUserClaims;
          this.customClaimsSubject.next(claims); // Update claims observable

          // Optionally, create an AppUser object with claims
          const appUser: AppUser = { ...user, claims };
          this.currentUserSubject.next(appUser);
          this.authResolvedSubject.next(true);

          this.analytics.setUser(user.uid);
          this.analytics.setUserProps({
            user_role: primaryUserRole(claims.roles),
            email_verified: user.emailVerified ? 'true' : 'false',
          });

          // Fetch user settings from Firestore
          this.db.getUserSettings(user.uid).subscribe(settings => {
            this.userSettingsSubject.next(settings ?? null);
          });

        } catch (error) {
          console.error('Error getting ID token result on auth state change:', error);
          this.customClaimsSubject.next(null);
          this.currentUserSubject.next(user as AppUser); // Still emit user, but without claims
          this.authResolvedSubject.next(true);
          this.userSettingsSubject.next(null);
          this.analytics.setUser(user.uid);
          this.analytics.setUserProps({
            user_role: 'unknown',
            email_verified: user.emailVerified ? 'true' : 'false',
          });
        }
      } else {
        this.customClaimsSubject.next(null);
        this.currentUserSubject.next(null);
        this.authResolvedSubject.next(true);
        this.userSettingsSubject.next(null);
        this.analytics.clearUserIdentity();
      }
    });
  }

  // // Observable to get the current user
  // readonly user$: Observable<User | null> = authState(this.auth);

  // Sign in with Google
  async googleSignIn(rememberMe: boolean = true): Promise<UserCredential | null> {
    const provider = new GoogleAuthProvider();
    try {
      // Set persistence based on "Remember me" preference
      const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(this.auth, persistence);
      const credential = await signInWithPopup(this.auth, provider);
      if (credential.user) {
        const { creationTime, lastSignInTime } = credential.user.metadata;
        const isFirstSignIn = creationTime === lastSignInTime;
        if (isFirstSignIn) {
          this.analytics.logEvent('sign_up_complete', { method: 'google' });
        } else {
          this.analytics.logEvent('login_success', { method: 'google' });
        }
      }
      return credential;
    } catch (error) {
      console.error("Google Sign-In error:", error);
      this.analytics.logEvent('login_fail', {
        method: 'google',
        error_code: authTelemetryCode(error),
      });
      throw error;
    }
  }

  // Sign in with Email and Password
  async emailSignIn(email: string, password: string, rememberMe: boolean = true): Promise<UserCredential | null> {
    try {
      // Set persistence based on "Remember me" preference
      const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
      await setPersistence(this.auth, persistence);
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      if (credential.user) {
        this.analytics.logEvent('login_success', { method: 'password' });
      }
      return credential;
    } catch (error) {
      console.error("Email Sign-In error:", error);
      this.analytics.logEvent('login_fail', {
        method: 'password',
        error_code: authTelemetryCode(error),
      });
      throw error;
    }
  }

  // Create User with Email and Password
  async createUserWithEmail(email: string, password: string): Promise<UserCredential | null> {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      if (credential.user) {
        this.analytics.logEvent('sign_up_complete', { method: 'password' });
      }
      return credential;
    } catch (error) {
      console.error("Create User error:", error);
      this.analytics.logEvent('sign_up_fail', {
        method: 'password',
        error_code: authTelemetryCode(error),
      });
      throw error;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    if (this.auth.currentUser) {
      this.analytics.logEvent('logout');
    }
    try {
      await signOut(this.auth);
    } catch (error) {
      console.error("Sign Out error:", error);
      // Optionally handle sign-out errors
    }
  }

  // Example: Get current user's roles from the observable
  getUserRoles(): Observable<string[] | undefined> {
    return this.customClaims$.pipe(
      map(claims => claims?.roles)
    );
  }

  // Example: Check if user has a specific role using the observable
  hasRole(role: string): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(roles => roles ? roles.includes(role) : false)
    );
  }

  // Check if user is an advisor (or admin)
  isAdvisor(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(roles => roles ? (roles.includes('advisor') || roles.includes('admin')) : false)
    );
  }

  // Check if user is a lender (or admin)
  isLender(): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(roles => roles ? (roles.includes('lender') || roles.includes('admin')) : false)
    );
  }

  // Check if user is an admin
  isAdmin(): Observable<boolean> {
    return this.hasRole('admin');
  }

  // Example: Get current user's roles directly (one-time fetch if needed, prefers observable)
  async fetchUserRolesDirectly(): Promise<string[] | undefined> {
    const user = this.auth.currentUser;
    if (user) {
      try {
        const idTokenResult: IdTokenResult = await getIdTokenResult(user);
        const claims = idTokenResult.claims as CustomUserClaims;
        return claims.roles;
      } catch (error) {
        console.error('Error getting user roles or claims directly:', error);
        return undefined;
      }
    }
    return undefined;
  }

  async refreshUserSettings() {
    const user = this.auth.currentUser;
    if (user) {
      this.db.getUserSettings(user.uid).subscribe(settings => {
        this.userSettingsSubject.next(settings ?? null);
      });
    }
  }

  // Re-authenticate user with email and password
  async reauthenticateWithPassword(password: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user is currently signed in or user has no email');
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    } catch (error) {
      console.error('Error re-authenticating user:', error);
      throw error;
    }
  }

  // Re-authenticate user with Google
  async reauthenticateWithGoogle(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(user, provider);
    } catch (error) {
      console.error('Error re-authenticating with Google:', error);
      throw error;
    }
  }

  async deleteUser(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      // Clear local state before deletion to prevent permission errors
      this.userSettingsSubject.next(null);
      this.customClaimsSubject.next(null);
      
      // Delete the user account from Firebase Auth
      await deleteUser(user);
      
      // Clear current user state
      this.currentUserSubject.next(null);
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw error;
    }
  }

  // Send email verification to current user
  async sendEmailVerification(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      await sendEmailVerification(user);
    } catch (error) {
      console.error('Error sending email verification:', error);
      throw error;
    }
  }

  // Send password reset email
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  // Check if current user's email is verified
  async checkEmailVerified(): Promise<boolean> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      // Reload the user to get the latest email verification status
      await reload(user);
      this.syncAnalyticsUserProperties();
      return user.emailVerified;
    } catch (error) {
      console.error('Error checking email verification:', error);
      throw error;
    }
  }

  /**
   * Refresh GA4 user id + properties and `currentUser$` from `auth.currentUser` after `reload()`
   * (email verification does not always trigger a new `onAuthStateChanged`).
   */
  syncAnalyticsUserProperties(): void {
    const user = this.auth.currentUser;
    if (!user) {
      this.analytics.clearUserIdentity();
      return;
    }
    const claims = this.customClaimsSubject.getValue() ?? undefined;
    this.analytics.setUser(user.uid);
    this.analytics.setUserProps({
      user_role: primaryUserRole(claims?.roles),
      email_verified: user.emailVerified ? 'true' : 'false',
    });
    this.currentUserSubject.next({ ...user, claims } as AppUser);
  }
}